import { useState } from 'react';
import { Wizard } from 'react-use-wizard';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi } from '../../services/api';
import type { DayOfWeek } from 'shared';

import { WelcomeStep } from './steps/WelcomeStep';
import { NameStep } from './steps/NameStep';
import { AddressStep } from './steps/AddressStep';
import { WorkDaysStep } from './steps/WorkDaysStep';
import { WorkHoursStep } from './steps/WorkHoursStep';
import { ConfirmationStep } from './steps/ConfirmationStep';
import { ProgressBar } from './ProgressBar';

export interface SetupData {
  name: string;
  home_address: string;
  home_lat: number | null;
  home_lng: number | null;
  work_days: DayOfWeek[];
  work_start_time: string;
  work_end_time: string;
}

const initialData: SetupData = {
  name: '',
  home_address: '',
  home_lat: null,
  home_lng: null,
  work_days: ['MON', 'TUE', 'WED', 'THU', 'FRI'],
  work_start_time: '09:00',
  work_end_time: '17:00',
};

export function SetupWizard() {
  const [data, setData] = useState<SetupData>(initialData);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const saveMutation = useMutation({
    mutationFn: () => userApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
      localStorage.setItem('setup_complete', 'true');
      navigate('/stores/find');
    },
  });

  const updateData = (updates: Partial<SetupData>) => {
    setData((prev) => ({ ...prev, ...updates }));
  };

  const handleComplete = () => {
    saveMutation.mutate();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col">
      <Wizard
        header={<ProgressBar />}
      >
        <WelcomeStep />
        <NameStep data={data} updateData={updateData} />
        <AddressStep data={data} updateData={updateData} />
        <WorkDaysStep data={data} updateData={updateData} />
        <WorkHoursStep data={data} updateData={updateData} />
        <ConfirmationStep
          data={data}
          onComplete={handleComplete}
          isLoading={saveMutation.isPending}
          error={saveMutation.error?.message}
        />
      </Wizard>
    </div>
  );
}
